import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import {
  Settings, Eye, CheckCircle, Search, Trash2, ChevronUp, ChevronDown, HelpCircle,
  CheckSquare, XCircle, Code, AlignLeft, FileText, Image, LayoutGrid, Type, Plus,
  Calculator, MessageSquare, ImageIcon, AlignCenter, AlignJustify, ListOrdered, List, Quote, Table,
  Minus, Hash, Paperclip, Undo, Redo, Copy, ArrowUp, ArrowDown,
  GripVertical, X, ChevronRight, Clock, AlertCircle, Check, Star, Edit3, MinusCircle,
  Settings2, EyeOff, Trash, MousePointer, Save, Upload, Code2, Lightbulb, Palette,
  Eraser
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import ImageUploadModal from '../../components/ImageUploadModal';
import ImageUploadDialog from '../../components/ImageUploadDialog';
import GraphicsQuillEditor from '../../components/GraphicsQuillEditor';
import QuillEditorWithImageResize from '../../components/QuillEditorWithImageResize';
import paperStorage from '../../utils/paperStorage';
import { supabase, getSupabaseUrl } from '../../supabase/client';
// Scratch types removed - scratch editor deprecated
type ScratchQuestionSubtype = 'algorithm' | 'operation' | 'creative';
type AlgorithmConfig = any;
type OperationConfig = any;
type CreativeConfig = any;

interface Question {
  id: string;
  type: string;
  subType?: 'scratch' | 'python' | 'cpp';
  name?: string; // 题目名称，用于后台列表识别，对应数据库 title 字段
  title: string;
  placeholder?: string;
  required: boolean;
  inputType?: string;
  options: Option[];
  score: number;
  showAnswer?: boolean;
  answerAnalysis?: string;
  titleImages: string[];
  titleImageWidths?: number[];
  imagePosition?: 'above' | 'below';
  titleImageLayout?: string;
  optionLayout?: string;
  answerImages?: string[];
  answerImageWidths?: number[];
  codeTemplate?: string;
  codeLanguage?: string;
  allowImageUpload?: boolean;
  maxImageSize?: number;
  allowTextInput?: boolean;
  maxTextLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  gradingMode?: 'manual' | 'ai';
  scoringPoints?: string;
  explanation?: string;
  answerText?: string;
  scoringPointsImages?: string[];
  scoringPointsImageWidths?: number[];
  referenceAnswerImages?: string[];
  referenceAnswerImageWidths?: number[];
  graphicsAnswerItems?: GraphicsAnswerItem[];
  graphicsDifficulty?: 'easy' | 'medium' | 'hard';
  graphicsCategory?: string;
  graphicsContent?: string;
  scratchAnswerItems?: ScratchAnswerItem[];
  // Scratch三种题型配置
  scratchSubtype?: ScratchQuestionSubtype;
  scratchConfig?: AlgorithmConfig | OperationConfig | CreativeConfig;
  referenceSb3Url?: string;
  optionImageWidths?: Record<string, number>;
}

interface ScratchAnswerItem {
  id: string;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  aiReferenceImage?: string;
  aiReferenceImageWidth?: number;
  aiScoringPoints?: string;
}

interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  image?: string;
  imageWidth?: number;
}

interface GraphicsAnswerItem {
  id: string;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  maxCount: number;
  aiReferenceImage?: string;
  aiScoringPoints?: string;
}

interface ImageUploadTarget {
  questionId: string;
  optionId?: string;
  existingImageIndex?: number;
}

interface ImageViewerState {
  isOpen: boolean;
  imageUrl: string;
  title: string;
  currentIndex: number;
  totalCount: number;
}

const questionTypes = [
  {
    category: '考试题型',
    items: [
      { icon: CheckSquare, label: '单选题', type: 'single_choice', placeholder: '请选择' },
      { icon: CheckCircle, label: '判断题', type: 'judge', placeholder: '请选择' },
      { icon: AlignLeft, label: '多选题', type: 'multi_choice', placeholder: '请选择' },
      { icon: Code, label: '主观题', type: 'subjective', placeholder: '请输入答案' },
      { icon: Code, label: 'Python编程题', type: 'python_coding', placeholder: '请输入Python代码' },
      { icon: Code, label: 'C++编程题', type: 'cpp_coding', placeholder: '请输入C++代码' },
      { icon: Code2, label: 'Scratch编程题', type: 'scratch_coding', placeholder: '请输入代码', scratchSubtype: 'algorithm' as ScratchQuestionSubtype }
    ]
  }
];

// Scratch编程子类型选项卡配置
const scratchSubTabs = [
  { key: 'algorithm', label: '算法题', icon: Code2 },
  { key: 'operation', label: '操作题', icon: MousePointer },
  { key: 'creative', label: '创意题', icon: Palette }
];

// 获取题型标签
const getQuestionTypeLabel = (type: string, scratchSubtype?: ScratchQuestionSubtype) => {
  if (type === 'scratch_coding') {
    switch (scratchSubtype) {
      case 'algorithm': return 'Scratch编程-算法题';
      case 'operation': return 'Scratch编程-操作题';
      case 'creative': return 'Scratch编程-创意题';
      default: return 'Scratch编程题';
    }
  }
  const typeMap: Record<string, string> = {
    'single_choice': '单选题',
    'multi_choice': '多选题',
    'judge': '判断题',
    'subjective': '主观题',
    'python_coding': 'Python编程题',
    'cpp_coding': 'C++编程题'
  };
  return typeMap[type] || '未知题型';
};

// 获取题型图标
const getQuestionTypeIcon = (type: string, scratchSubtype?: ScratchQuestionSubtype) => {
  if (type === 'scratch_coding') {
    switch (scratchSubtype) {
      case 'algorithm': return Code2;
      case 'operation': return MousePointer;
      case 'creative': return Palette;
      default: return Code;
    }
  }
  const iconMap: Record<string, any> = {
    'single_choice': CheckSquare,
    'multi_choice': AlignLeft,
    'judge': CheckCircle,
    'subjective': AlignLeft,
    'python_coding': Code,
    'cpp_coding': Code
  };
  return iconMap[type] || HelpCircle;
};

export default function PaperEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editPaperId = searchParams.get('edit');
  const pendingPaperId = searchParams.get('pending');
  const targetQuestionId = searchParams.get('question');
  const [isPendingPaper, setIsPendingPaper] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [remark, setRemark] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('type');
  const [showScoreDropdown, setShowScoreDropdown] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUploadTarget, setImageUploadTarget] = useState<ImageUploadTarget | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [imageWidth, setImageWidth] = useState(100);
  const [imageViewer, setImageViewer] = useState<ImageViewerState>({ isOpen: false, imageUrl: '', title: '', currentIndex: 0, totalCount: 0 });
  const [showImageUploadDialog, setShowImageUploadDialog] = useState(false);
  const [imageUploadDialogConfig, setImageUploadDialogConfig] = useState<{
    title: string;
    target: 'title' | 'answer' | 'scoringPoints' | 'referenceAnswer';
    questionId: string;
  } | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showWordImportModal, setShowWordImportModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [saveConfirmAction, setSaveConfirmAction] = useState<'draft' | 'saved' | 'published'>('draft');
  const [isSaving, setIsSaving] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedQuestion, setDraggedQuestion] = useState<Question | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const [paperSettings, setPaperSettings] = useState({
    year: '',
    month: '',
    organization: '',
    source: '',
    nature: '付费',
    categoryType: '',
    level_text: '',
    paperType: '',
    duration: '60'
  });

  const generateAutoTitle = useCallback(() => {
    const { year, month, organization, categoryType, level_text, source } = paperSettings;
    if (!year || !organization || !categoryType || !level_text || !source) {
      return '';
    }
    const monthPart = month && month !== '0' ? `${month}月` : '';
    const categoryName = categoryStore.getCategoryNameByCode(categoryType) || categoryType;
    return `${year}年${monthPart}${organization}${categoryName}${level_text}${source}`;
  }, [paperSettings]);

  useEffect(() => {
    const autoTitle = generateAutoTitle();
    setTitle(autoTitle);
  }, [paperSettings, generateAutoTitle]);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [cats, setCats] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);

  const selectedOrg = orgs.find(o => o.name === paperSettings.organization);
  const filteredCats = selectedOrg
    ? cats.filter(c => c.orgId === selectedOrg.id)
    : [];

  const selectedCat = filteredCats.find(c => c.name === paperSettings.categoryType);
  const filteredLevels = selectedCat
    ? levels.filter(l => l.catId === selectedCat.id)
    : [];

  const years = ['', ...Array.from({ length: 21 }, (_, i) => String(2010 + i))];
  const months = [{ value: 0, label: '无' }, ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}月` }))];
  const sources = ['真题', '模拟题'];
  const natures = ['免费', '付费'];
  const paperTypes = ['练习', '考试'];

  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const draft = params.get('draft');
    if (draft) setDraftId(draft);
  }, []);

  useEffect(() => {
    fetchCategories();
    if (editPaperId) {
      loadPaperForEdit(editPaperId);
    } else if (pendingPaperId) {
      loadPendingPaperData(pendingPaperId);
    } else if (draftId) {
      loadPaperDraftData(draftId);
    } else {
      // temp_imported_papers 和 temp_graphics_questions 表尚未创建，暂时跳过查询避免 404
      // loadImportedDataFromSupabase();
      // loadGraphicsQuestionFromSupabase();
      loadEditorStateFromStorage();
    }
  }, [editPaperId, pendingPaperId, draftId]);

  const loadImportedDataFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('temp_imported_papers')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        // 表不存在时静默跳过，不产生 404 错误日志
        if (error.code === '42P01' || error.message?.includes('does not exist') || error.status === 404) return;
        throw error;
      }
      if (data) {
        loadImportedPaperData(data.paper_data);
        await supabase.from('temp_imported_papers').delete().eq('id', data.id);
      }
    } catch (e) {
      console.error('加载导入数据失败:', e);
    }
  };

  const loadEditorStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem('paperEditorState');
      if (savedState) {
        const state = JSON.parse(savedState);
        if (state.title) setTitle(state.title);
        if (state.description) setDescription(state.description);
        if (state.questions && state.questions.length > 0) setQuestions(state.questions);
        if (state.paperSettings) setPaperSettings(state.paperSettings);
        if (state.selectedCategory) setSelectedCategory(state.selectedCategory);
        if (state.isPendingPaper) setIsPendingPaper(state.isPendingPaper);
        if (state.pendingPaperId) setIsPendingPaper(state.pendingPaperId);
        if (state.editPaperId) setIsPendingPaper(state.editPaperId);
        localStorage.removeItem('paperEditorState');
      }

      // 从预览界面回传的图片宽度调整数据
      const widthUpdatesStr = localStorage.getItem('previewImageWidthUpdates');
      if (widthUpdatesStr) {
        const widthUpdates = JSON.parse(widthUpdatesStr);
        if (widthUpdates && Array.isArray(widthUpdates) && widthUpdates.length > 0) {
          setQuestions(prevQuestions => {
            const updatedQuestions = prevQuestions.map(q => {
              const update = widthUpdates.find(u => u.id === q.id);
              if (!update) return q;

              const updatedQ = { ...q };

              // 同步题干图片宽度
              if (update.titleImageWidths && update.titleImageWidths.length > 0) {
                updatedQ.titleImageWidths = update.titleImageWidths;
              }

              // 同步选项图片宽度
              if (update.optionImageWidths && Array.isArray(update.optionImageWidths)) {
                const updatedOptions = q.options.map(opt => {
                  const optUpdate = update.optionImageWidths.find(ow => ow.id === opt.id);
                  if (optUpdate && optUpdate.imageWidth !== undefined) {
                    return { ...opt, imageWidth: optUpdate.imageWidth };
                  }
                  return opt;
                });
                updatedQ.options = updatedOptions;
              }

              return updatedQ;
            });
            return updatedQuestions;
          });
        }
        localStorage.removeItem('previewImageWidthUpdates');
      }
    } catch (e) {
      console.error('加载编辑器状态失败:', e);
    }
  };

  const loadGraphicsQuestionFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('temp_graphics_questions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        // 表不存在时静默跳过，不产生 404 错误日志
        if (error.code === '42P01' || error.message?.includes('does not exist') || error.status === 404) return;
        throw error;
      }
      if (data) {
        addGraphicsQuestionToPaper(data.question_data);
        await supabase.from('temp_graphics_questions').delete().eq('id', data.id);
      }
    } catch (e) {
      console.error('加载导入数据失败:', e);
    }
  };

  const loadImportedPaperData = (data: any) => {
    if (data.title) {
      setTitle(data.title);
    }
    if (data.settings) {
      setPaperSettings({
        year: data.settings.year || '',
        month: data.settings.month || '',
        organization: data.settings.organization || '',
        source: data.settings.source || '',
        nature: data.settings.nature || '',
        categoryType: data.settings.categoryType || '',
        level_text: data.settings.level_text || '',
        paperType: data.settings.paperType || '',
        duration: data.settings.duration || ''
      });
    }
    if (data.questions && Array.isArray(data.questions)) {
      const importedQuestions: Question[] = data.questions.map((q: any, index: number) => ({
        id: Date.now().toString() + index,
        type: q.type || 'single_choice',
        subType: q.subType,
        title: q.title || '',
        placeholder: '',
        required: true,
        inputType: '文本',
        options: q.options || [],
        score: q.score || 0,
        showAnswer: false,
        titleImages: q.titleImages || [],
        titleImageWidths: q.titleImageWidths || [],
        answerImages: q.answerImages || [],
        answerImageWidths: q.answerImageWidths || [],
        codeTemplate: q.codeTemplate || '',
        codeLanguage: q.codeLanguage || '',
        allowImageUpload: q.allowImageUpload !== false,
        maxImageSize: q.maxImageSize || 5,
        allowTextInput: q.allowTextInput !== false,
        maxTextLength: q.maxTextLength || 800,
        allowFileUpload: q.allowFileUpload || false,
        maxFileSize: q.maxFileSize || 10,
        gradingMode: 'ai',
        scoringPoints: q.scoringPoints || '',
        explanation: q.explanation || '',
        answerText: q.answerText || '',
        scoringPointsImages: q.scoringPointsImages || [],
        scoringPointsImageWidths: q.scoringPointsImageWidths || [],
        referenceAnswerImages: q.referenceAnswerImages || [],
        referenceAnswerImageWidths: q.referenceAnswerImageWidths || [],
        scratchSubtype: q.scratchSubtype,
        scratchConfig: q.scratchConfig,
        referenceSb3Url: q.referenceSb3Url
      }));
      setQuestions(importedQuestions);
    }
  };

  const addGraphicsQuestionToPaper = (graphicsQ: any) => {
    const newQuestion: Question = {
      id: graphicsQ.id,
      type: 'scratch_coding',
      subType: 'scratch',
      title: graphicsQ.title,
      placeholder: '请输入代码',
      required: true,
      inputType: '文本',
      options: [],
      score: graphicsQ.score || 15,
      showAnswer: false,
      titleImages: [],
      titleImageWidths: [],
      imagePosition: 'below',
      answerImages: [],
      answerImageWidths: [],
      codeTemplate: '',
      codeLanguage: 'scratch',
      allowImageUpload: true,
      maxImageSize: 5,
      allowTextInput: false,
      maxTextLength: 0,
      allowFileUpload: false,
      maxFileSize: 0,
      gradingMode: 'ai',
      scoringPoints: graphicsQ.answerItems?.map((item: any) => item.name).join('、') || '',
      explanation: graphicsQ.content || '',
      answerText: '',
      scoringPointsImages: [],
      scoringPointsImageWidths: [],
      referenceAnswerImages: [],
      referenceAnswerImageWidths: [],
      scratchAnswerItems: graphicsQ.answerItems?.map((item: any, idx: number) => ({
        id: item.id || String(idx + 1),
        name: item.name || `作答项${idx + 1}`,
        placeholder: item.placeholder || '请上传程序截图',
        required: true,
        maxSizeMB: 5
      })) || [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }],
      scratchSubtype: graphicsQ.scratchSubtype || 'operation',
      scratchConfig: graphicsQ.scratchConfig
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
    setEditingQuestion(newQuestion.id);
  };

  const loadPaperForEdit = async (paperId: string) => {
    try {
      const { data: paper, error } = await supabase
        .from('papers')
        .select('*')
        .eq('id', paperId)
        .single();

      if (error || !paper) {
        alert('加载试卷失败');
        return;
      }

      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('*')
        .eq('paper_id', paperId)
        .in('status', ['published', 'draft', 'hidden'])
        .order('order_index', { ascending: true });

      if (questionsError) {
        console.error('加载题目失败:', questionsError);
      }

      setTitle(paper.title || '');
      setDescription(paper.description || '');
      setRemark(paper.remark || '');
      setPaperSettings({
        year: String(paper.year || ''),
        month: paper.month ? String(paper.month) : '',
        organization: paper.organization || '',
        source: paper.paper_type === 'real' ? '真题' : paper.paper_type === 'mock' ? '模拟题' : '',
        nature: paper.is_free !== false ? '免费' : '付费',
        categoryType: categoryStore.getCategoryNameByCode(paper.category_type) || paper.category_type || '',
        level_text: paper.level_text || '',
        paperType: '',
        duration: paper.duration ? String(paper.duration) : ''
      });
      setSelectedCategory(paper.category_id || '');
      setIsPendingPaper(false);

      if (questionsData && questionsData.length > 0) {
        const convertedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          const typeMap: Record<string, string> = {
            'single_choice': 'single_choice',
            'multiple_choice': 'multi_choice',
            'judge': 'judge',
            'python': 'python_coding',
            'cpp': 'cpp_coding',
            'graphic': 'scratch_coding',
            'graphics': 'scratch_coding',
            'python_coding': 'python_coding',
            'cpp_coding': 'cpp_coding',
            'scratch_coding': 'scratch_coding',
            'coding': 'python_coding'
          };

          const mappedType = typeMap[q.question_type] || 'single_choice';
          const isScratchCoding = mappedType === 'scratch_coding';

          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: mappedType,
            subType: isScratchCoding ? 'scratch' : undefined,
            name: q.title || '', // 从数据库 title 字段回显到 name
            title: (() => {
              let titleContent = q.content || '';
              const widths = q.title_image_widths;
              if (titleContent.includes('<img') && widths && widths.length > 0) {
                let imgIndex = 0;
                titleContent = titleContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = widths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return titleContent;
            })(),
            placeholder: isScratchCoding ? '请输入代码' : '',
            required: true,
            inputType: isScratchCoding ? '文本' : '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              image: opt.image || opt.image_url || '',
              imageWidth: opt.imageWidth || opt.image_width || 100
            })),
            score: q.score || (isScratchCoding ? 15 : 2),
            showAnswer: false,
            titleImages: q.title_images || [],
            titleImageWidths: q.title_image_widths || q.title_images?.map(() => 50) || [],
            imagePosition: q.image_position || 'below',
            titleImageLayout: q.title_image_layout || 'auto',
            optionLayout: q.option_layout || 'grid',
            optionImageWidths: q.option_image_widths || {},
            answerImages: [],
            answerImageWidths: [],
            codeTemplate: '',
            codeLanguage: isScratchCoding ? 'scratch' : '',
            allowImageUpload: isScratchCoding,
            maxImageSize: 5,
            allowTextInput: !isScratchCoding,
            maxTextLength: isScratchCoding ? 0 : 800,
            allowFileUpload: false,
            maxFileSize: 10,
            gradingMode: 'ai',
            scoringPoints: q.scoring_points || '',
            explanation: q.explanation || '',
            answerText: q.reference_answer || '',
            scoringPointsImages: [],
            scoringPointsImageWidths: [],
            referenceAnswerImages: [],
            referenceAnswerImageWidths: [],
            scratchAnswerItems: isScratchCoding ? [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }] : undefined,
            scratchSubtype: q.scratch_subtype as ScratchQuestionSubtype,
            scratchConfig: q.scratch_config,
            referenceSb3Url: q.reference_sb3_url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));

        // 如果有目标题目ID，自动定位到该题目
        if (targetQuestionId) {
          setTimeout(() => {
            const questionElement = document.getElementById(`question-${targetQuestionId}`);
            if (questionElement) {
              questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // 高亮显示该题目
              questionElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
              setTimeout(() => {
                questionElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
              }, 3000);
            }
          }, 500);
        }
      } else {
        setQuestions([]);
      }
    } catch (err) {
      alert('加载试卷异常');
    }
  };

  const loadPaperDraftData = async (draftId: string) => {
    try {
      const { data: draft, error: draftError } = await supabase
        .from('paper_drafts')
        .select('*')
        .eq('id', draftId)
        .single();

      if (draftError || !draft) {
        alert('加载草稿失败');
        return;
      }

      setTitle(draft.title || '');
      setRemark(draft.remark || '');
      setIsPendingPaper(true);

      if (draft.questions && Array.isArray(draft.questions)) {
        const convertedQuestions: Question[] = draft.questions.map((q: any, index: number) => {
          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: q.type || 'single_choice',
            subType: q.subType,
            title: q.title || '',
            placeholder: q.placeholder || '请选择',
            required: true,
            inputType: q.inputType || '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || opt.label || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              image: opt.image || opt.image_url || '',
              imageWidth: opt.imageWidth || opt.image_width || 100
            })),
            score: q.score || 2,
            showAnswer: false,
            titleImages: q.titleImages || [],
            titleImageWidths: q.titleImageWidths || [],
            imagePosition: q.imagePosition || 'below',
            titleImageLayout: q.titleImageLayout || 'auto',
            optionLayout: q.optionLayout || 'grid',
            answerImages: q.answerImages || [],
            answerImageWidths: q.answerImageWidths || [],
            codeTemplate: q.codeTemplate || '',
            codeLanguage: q.codeLanguage || '',
            allowImageUpload: q.allowImageUpload || false,
            maxImageSize: q.maxImageSize || 5,
            allowTextInput: q.allowTextInput !== false,
            maxTextLength: q.maxTextLength || 800,
            allowFileUpload: q.allowFileUpload || false,
            maxFileSize: q.maxFileSize || 10,
            gradingMode: q.gradingMode || 'manual',
            scoringPoints: q.scoringPoints || '',
            explanation: q.explanation || '',
            answerText: q.answerText || '',
            scoringPointsImages: q.scoringPointsImages || [],
            scoringPointsImageWidths: q.scoringPointsImageWidths || [],
            referenceAnswerImages: q.referenceAnswerImages || [],
            referenceAnswerImageWidths: q.referenceAnswerImageWidths || [],
            scratchAnswerItems: q.scratchAnswerItems,
            scratchSubtype: q.scratchSubtype,
            scratchConfig: q.scratchConfig,
            referenceSb3Url: q.referenceSb3Url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));
      }
    } catch (err) {
      alert('加载草稿异常');
    }
  };

  const loadPendingPaperData = async (pendingId: string) => {
    try {
      const { data: pendingPaper, error: paperError } = await supabase
        .from('pending_papers')
        .select('*')
        .eq('id', pendingId)
        .single();

      if (paperError || !pendingPaper) {
        alert('加载待编辑试卷失败');
        return;
      }

      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('*')
        .eq('pending_paper_id', pendingId)
        .order('order_index', { ascending: true });

      if (questionsError) {
        console.error('加载题目失败:', questionsError);
      }

      setTitle(pendingPaper.title || '');
      setRemark(pendingPaper.remark || '');
      setIsPendingPaper(true);

      // 设置试卷配置信息
      setPaperSettings({
        year: String(pendingPaper.year || ''),
        month: pendingPaper.month ? String(pendingPaper.month) : '',
        organization: pendingPaper.organization || '',
        source: pendingPaper.paper_type === 'real' ? '真题' : pendingPaper.paper_type === 'mock' ? '模拟题' : '',
        nature: pendingPaper.is_free === true ? '免费' : '付费',
        categoryType: categoryStore.getCategoryNameByCode(pendingPaper.category_type) || pendingPaper.category_type || '',
        level_text: pendingPaper.level_text || '',
        paperType: '',
        duration: pendingPaper.duration ? String(pendingPaper.duration) : ''
      });

      if (questionsData && questionsData.length > 0) {
        const convertedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          const typeMap: Record<string, string> = {
            'single_choice': 'single_choice',
            'multiple_choice': 'multi_choice',
            'judge': 'judge',
            'python': 'python_coding',
            'cpp': 'cpp_coding',
            'graphic': 'scratch_coding',
            'graphics': 'scratch_coding'
          };

          const mappedType = typeMap[q.question_type] || 'single_choice';
          const isScratchCoding = mappedType === 'scratch_coding';

          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: mappedType,
            subType: isScratchCoding ? 'scratch' : undefined,
            name: q.title || '', // 从数据库 title 字段回显到 name
            title: (() => {
              let titleContent = q.content || '';
              const widths = q.title_image_widths;
              if (titleContent.includes('<img') && widths && widths.length > 0) {
                let imgIndex = 0;
                titleContent = titleContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = widths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return titleContent;
            })(),
            placeholder: isScratchCoding ? '请输入代码' : '',
            required: true,
            inputType: isScratchCoding ? '文本' : '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              image: opt.image || opt.image_url || '',
              imageWidth: opt.imageWidth || opt.image_width || 100
            })),
            score: q.score || (isScratchCoding ? 15 : 2),
            showAnswer: false,
            titleImages: q.title_images || [],
            titleImageWidths: q.title_image_widths || q.title_images?.map(() => 50) || [],
            imagePosition: q.image_position || 'below',
            titleImageLayout: q.title_image_layout || 'auto',
            optionLayout: q.option_layout || 'grid',
            optionImageWidths: q.option_image_widths || {},
            answerImages: [],
            answerImageWidths: [],
            codeTemplate: '',
            codeLanguage: isScratchCoding ? 'scratch' : '',
            allowImageUpload: isScratchCoding,
            maxImageSize: 5,
            allowTextInput: !isScratchCoding,
            maxTextLength: isScratchCoding ? 0 : 800,
            allowFileUpload: false,
            maxFileSize: 10,
            gradingMode: 'ai',
            scoringPoints: '',
            explanation: q.explanation || '',
            answerText: '',
            scoringPointsImages: [],
            scoringPointsImageWidths: [],
            referenceAnswerImages: [],
            referenceAnswerImageWidths: [],
            scratchAnswerItems: isScratchCoding ? [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }] : undefined,
            scratchSubtype: q.scratch_subtype as ScratchQuestionSubtype,
            scratchConfig: q.scratch_config,
            referenceSb3Url: q.reference_sb3_url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));
      }
    } catch (err) {
      alert('加载待编辑试卷异常');
    }
  };

  const publishPendingPaper = async () => {
    if (isSaving) return;
    if (!isAllSettingsSelected()) {
      alert('请完整填写所有试卷设置信息（年份、月份、考评体系、课程分类、级别分类、试卷类型、免费/付费、考试时长）');
      return;
    }

    if (!pendingPaperId) {
      alert('待编辑试卷ID不存在');
      return;
    }

    const selectedLevelName = paperSettings.level_text;
    const validLevelNames = levels.map(l => l.name);
    if (!validLevelNames.includes(selectedLevelName)) {
      alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
      return;
    }

    setIsSaving(true);
    try {
      const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
      const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
      const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
      const judgeCount = questions.filter(q => q.type === 'judge').length;
      const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding'].includes(q.type)).length;
      const newPaperId = generateUUID();

      const autoTitle = generateAutoTitle();
      const fullTitle = title || autoTitle;

      const { data: { session } } = await supabase.auth.getSession();
      const adminId = session?.user?.id || 'admin';
      const adminName = '管理员';
      const adminUsername = 'admin';
      const currentTime = new Date().toISOString();

      const paperData = {
        id: newPaperId,
        title: fullTitle,
        description,
        year: paperSettings.year,
        month: paperSettings.month,
        organization: paperSettings.organization,
        source: paperSettings.source,
        nature: paperSettings.nature,
        total_score: totalScore,
        question_count: questions.filter(q => q.type !== 'paragraph').length,
        questions: questions.map(q => {
          const baseQuestion = {
            id: q.id,
            type: q.type,
            subType: q.subType,
            title: q.title,
            placeholder: q.placeholder,
            required: q.required,
            inputType: q.inputType,
            options: q.options,
            score: q.score,
            showAnswer: q.showAnswer,
            titleImages: q.titleImages,
            titleImageWidths: q.titleImageWidths,
            answerImages: q.answerImages,
            answerImageWidths: q.answerImageWidths,
            codeTemplate: q.codeTemplate,
            codeLanguage: q.codeLanguage,
            allowImageUpload: q.allowImageUpload,
            maxImageSize: q.maxImageSize,
            allowTextInput: q.allowTextInput,
            maxTextLength: q.maxTextLength,
            allowFileUpload: q.allowFileUpload,
            maxFileSize: q.maxFileSize,
            gradingMode: q.gradingMode,
            scoringPoints: q.scoringPoints,
            scoringPointsImages: q.scoringPointsImages,
            scoringPointsImageWidths: q.scoringPointsImageWidths,
            explanation: q.explanation,
            answerText: q.answerText,
            answerAnalysis: q.answerAnalysis,
            referenceAnswer: q.answerText,
            referenceAnswerImages: q.referenceAnswerImages,
            referenceAnswerImageWidths: q.referenceAnswerImageWidths,
            scratchSubtype: q.scratchSubtype,
            scratchConfig: q.scratchConfig,
            referenceSb3Url: q.referenceSb3Url
          };
          if (q.type === 'scratch_coding') {
            return {
              ...baseQuestion,
              scratchAnswerItems: q.scratchAnswerItems
            };
          }
          return baseQuestion;
        }),
        category_id: selectedCategory || null,
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text,
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        is_published: true,
        is_free: paperSettings.nature === '免费',
        remark: remark || '',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        status: 'published',
        created_at: currentTime,
        updated_at: currentTime,
        created_by: adminName,
        edit_history: [{ time: currentTime, user: adminUsername, type: '创建' }],
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount
      };

      const result = paperStorage.savePaper(paperData);
      if (!result.success) {
        alert(result.message || '保存失败');
        return;
      }

      await supabase
        .from('pending_papers')
        .update({ status: 'published' })
        .eq('id', pendingPaperId);

      await supabase
        .from('questions')
        .update({ status: 'published' })
        .eq('pending_paper_id', pendingPaperId);

      alert('发布成功！试卷已保存到试卷列表');
      navigate('/admin/papers');
    } catch (err) {
      alert('发布失败，请稍后重试');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchCategories = async () => {
    const { data: catsData } = await supabase.from('course_categories').select('*');
    const cats = catsData || [];
    setCategories(cats);
  };

  useEffect(() => {
    const loadCategories = async () => {
      await categoryStore.init();
      setOrgs(categoryStore.getOrganizations());
      setCats(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    };
    loadCategories();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrgs(categoryStore.getOrganizations());
      setCats(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });
    return unsubscribe;
  }, []);

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const isAllSettingsSelected = () => {
    return paperSettings.year && paperSettings.organization && paperSettings.categoryType &&
           paperSettings.level_text && paperSettings.source && paperSettings.nature && paperSettings.duration;
  };

  const savePaper = async (status: 'draft' | 'published' | 'saved') => {
    if (isSaving) return;
    if (!isAllSettingsSelected()) {
      alert('请完整填写所有试卷设置信息（年份、月份、考评体系、课程分类、级别分类、试卷类型、免费/付费、考试时长）');
      return;
    }

    const selectedLevelName = paperSettings.level_text;
    const validLevelNames = levels.map(l => l.name);
    if (!validLevelNames.includes(selectedLevelName)) {
      alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
      return;
    }

    setIsSaving(true);
    const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
    const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
    const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
    const judgeCount = questions.filter(q => q.type === 'judge').length;
    const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding'].includes(q.type)).length;
    const paperId = editPaperId || generateUUID();

    const autoTitle = generateAutoTitle();
    const fullTitle = title?.trim() || autoTitle || '未命名试卷';

    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id || 'admin';
    const adminName = '管理员';
    const currentTime = new Date().toISOString();

    try {
      if (isPendingPaper && pendingPaperId && status !== 'draft') {
        await supabase.from('pending_papers').delete().eq('id', pendingPaperId);
        await supabase.from('questions').delete().eq('pending_paper_id', pendingPaperId);
      }

      if (editPaperId && status === 'draft') {
        await supabase.from('papers').delete().eq('id', editPaperId);
        await supabase.from('questions').delete().eq('paper_id', editPaperId);
      }

      const paperData = {
        id: paperId,
        title: fullTitle,
        description,
        year: parseInt(paperSettings.year),
        month: paperSettings.month ? parseInt(paperSettings.month) : null,
        organization: paperSettings.organization,
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text,
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        is_free: paperSettings.nature === '免费',
        is_published: status === 'published',
        status: status === 'saved' ? 'hidden' : status,
        total_score: totalScore,
        question_count: questions.length,
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount,
        remark: remark || '',
        created_at: currentTime,
        updated_at: currentTime
      };

      if (editPaperId && status !== 'draft') {
        const { error } = await supabase.from('papers').update(paperData).eq('id', paperId);
        if (error) throw error;
        await supabase.from('questions').delete().eq('paper_id', paperId);
      } else {
        const { error } = await supabase.from('papers').insert(paperData);
        if (error) throw error;
      }

      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];

        let processedTitleImages = q.titleImages || [];
        let processedTitleImageWidths = q.titleImageWidths || [];
        let processedTitle = q.title || '';

        const questionData = {
          paper_id: paperId,
          title: q.name || '', // 题目名称，用于后台列表识别
          content: processedTitle,
          question_type: q.type,
          score: q.score,
          order_index: i + 1,
          options: q.options || [],
          correct_answer: q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id).join(','),
          explanation: q.explanation || '',
          reference_answer: q.answerText || '',
          scoring_points: q.scoringPoints || '',
          title_images: processedTitleImages,
          title_image_widths: processedTitleImageWidths,
          option_image_widths: q.optionImageWidths || {},
          title_image_layout: q.titleImageLayout || 'auto',
          option_layout: q.optionLayout || 'grid',
          image_position: q.imagePosition || 'below',
          status: status === 'saved' ? 'hidden' : status,
          created_at: currentTime,
          updated_at: currentTime,
          scratch_subtype: q.scratchSubtype,
          scratch_config: q.scratchConfig,
          reference_sb3_url: q.referenceSb3Url
        };

        const { error: qError } = await supabase.from('questions').insert(questionData);
        if (qError) {
          
          throw qError;
        }
      }

      alert(editPaperId ? '试卷已更新' : '试卷已保存');
      navigate('/admin/papers');
    } catch (err: any) {
      
      alert('保存失败: ' + (err?.message || '未知错误'));
    } finally {
      setIsSaving(false);
    }
  };

  const publishPaper = () => savePaper('published');

  const saveAsDraft = async () => {
    if (isSaving) return;
    if (paperSettings.level_text) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
    const paperId = editPaperId || generateUUID();
    const currentTime = new Date().toISOString();

    try {
      const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
      const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
      const judgeCount = questions.filter(q => q.type === 'judge').length;
      const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding'].includes(q.type)).length;

      const autoTitle = generateAutoTitle();
      const finalTitle = title?.trim() || autoTitle || '未命名试卷';

      const paperData: any = {
        id: paperId,
        title: finalTitle,
        description: description || '',
        year: paperSettings.year ? parseInt(paperSettings.year) : null,
        month: paperSettings.month ? parseInt(paperSettings.month) : null,
        organization: paperSettings.organization || '',
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text || '',
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        is_free: paperSettings.nature === '免费',
        is_published: false,
        status: 'draft',
        question_count: questions.length,
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount,
        total_score: totalScore,
        updated_at: currentTime,
        remark: remark || ''
      };

      if (editPaperId) {
        const { error } = await supabase.from('papers').update(paperData).eq('id', paperId);
        if (error) throw error;
        await supabase.from('questions').delete().eq('paper_id', paperId);
      } else {
        paperData.created_at = currentTime;
        paperData.remark = remark || '';
        const { error } = await supabase.from('papers').insert(paperData);
        if (error) throw error;
      }

      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];

        let processedTitleImages = q.titleImages || [];
        let processedTitleImageWidths = q.titleImageWidths || [];
        let processedTitle = q.title || '';

        const questionData: any = {
          paper_id: paperId,
          title: q.name || '', // 题目名称，用于后台列表识别
          content: processedTitle,
          question_type: q.type,
          score: q.score,
          order_index: i + 1,
          options: q.options || [],
          correct_answer: q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id).join(','),
          explanation: q.explanation || '',
          reference_answer: q.answerText || '',
          scoring_points: q.scoringPoints || '',
          title_images: processedTitleImages,
          title_image_widths: processedTitleImageWidths,
          option_image_widths: q.optionImageWidths || {},
          title_image_layout: q.titleImageLayout || 'auto',
          option_layout: q.optionLayout || 'grid',
          image_position: q.imagePosition || 'below',
          status: 'draft',
          created_at: currentTime,
          updated_at: currentTime,
          scratch_subtype: q.scratchSubtype,
          scratch_config: q.scratchConfig,
          reference_sb3_url: q.referenceSb3Url
        };

        const { error: qError } = await supabase.from('questions').insert(questionData);
        if (qError) {
          
          throw qError;
        }
      }

      alert(editPaperId ? '试卷已转为草稿' : '已暂存为草稿');
      navigate('/admin/pending-papers');
    } catch (err: any) {
      
      alert('暂存草稿失败: ' + (err?.message || err?.error_description || '未知错误'));
    } finally {
      setIsSaving(false);
    }
  };

  const openImageViewer = (imageUrl: string, title: string, currentIndex: number = 0, totalCount: number = 1) => {
    setImageViewer({ isOpen: true, imageUrl, title, currentIndex, totalCount });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, imageUrl: '', title: '', currentIndex: 0, totalCount: 0 });
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImageViewer();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const sortQuestionsByType = (qs: Question[]) => {
    const typeOrder: Record<string, number> = {
      'single_choice': 1,
      'multi_choice': 2,
      'judge': 3,
      'python_coding': 4,
      'cpp_coding': 5,
      'scratch_coding': 6,
      'subjective': 7
    };
    return [...qs].sort((a, b) => {
      const orderA = typeOrder[a.type] || 99;
      const orderB = typeOrder[b.type] || 99;
      return orderA - orderB;
    });
  };

  // 创建默认配置
  const createDefaultConfig = (subtype: ScratchQuestionSubtype): AlgorithmConfig | OperationConfig | CreativeConfig => {
    switch (subtype) {
      case 'algorithm':
        return {
          requiredBlocks: [],
          forbiddenBlocks: [],
          minBlockCount: 5,
          maxBlockCount: 100,
          requiredConcepts: [],
          efficiencyWeight: 0.3,
          logicWeight: 0.5,
          structureWeight: 0.2
        };
      case 'operation':
        return {
          requiredSprites: [],
          requiredScripts: [],
          initialState: { x: 0, y: 0, direction: 90, visible: true },
          targetState: { x: 100, y: 100, direction: 90, visible: true },
          movementBlocks: ['motion_movesteps', 'motion_goto', 'motion_glideto'],
          tolerance: 10
        };
      case 'creative':
        return {
          minSprites: 1,
          minScripts: 1,
          requiredElements: [],
          creativityWeight: 0.3,
          techniqueWeight: 0.2,
          aestheticsWeight: 0.1,
          functionalityWeight: 0.4
        };
      default:
        return {} as AlgorithmConfig;
    }
  };

  const addQuestion = (typeItem: any) => {
    if (typeItem.type === 'python_coding' || typeItem.type === 'cpp_coding') {
      const newQuestion: Question = {
        id: Date.now().toString(),
        type: typeItem.type,
        subType: typeItem.type === 'python_coding' ? 'python' : 'cpp',
        title: '',
        placeholder: typeItem.placeholder,
        required: true,
        inputType: '文本',
        options: [],
        score: 15,
        showAnswer: false,
        titleImages: [],
        titleImageWidths: [],
        answerImages: [],
        answerImageWidths: [],
        codeTemplate: '',
        codeLanguage: typeItem.type === 'python_coding' ? 'python' : 'cpp',
        allowImageUpload: false,
        maxImageSize: 0,
        allowTextInput: true,
        maxTextLength: 800,
        allowFileUpload: false,
        maxFileSize: 0,
        gradingMode: 'ai',
        scoringPoints: '',
        explanation: '',
        answerText: '',
        scoringPointsImages: [],
        scoringPointsImageWidths: [],
        referenceAnswerImages: [],
        referenceAnswerImageWidths: []
      };
      setQuestions(sortQuestionsByType([...questions, newQuestion]));
      setEditingQuestion(newQuestion.id);
      return;
    }

    if (typeItem.type === 'scratch_coding') {
      const subtype = typeItem.scratchSubtype || 'operation';
      const newQuestion: Question = {
        id: Date.now().toString(),
        type: 'scratch_coding',
        subType: 'scratch',
        title: '',
        placeholder: '请输入代码',
        required: true,
        inputType: '文本',
        options: [],
        score: 15,
        showAnswer: false,
        titleImages: [],
        titleImageWidths: [],
        answerImages: [],
        answerImageWidths: [],
        codeTemplate: '',
        codeLanguage: 'scratch',
        allowImageUpload: true,
        maxImageSize: 5,
        allowTextInput: false,
        maxTextLength: 0,
        allowFileUpload: false,
        maxFileSize: 0,
        gradingMode: 'ai',
        scoringPoints: '',
        explanation: '',
        answerText: '',
        scoringPointsImages: [],
        scoringPointsImageWidths: [],
        referenceAnswerImages: [],
        referenceAnswerImageWidths: [],
        scratchAnswerItems: [
          { id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }
        ],
        scratchSubtype: subtype,
        scratchConfig: createDefaultConfig(subtype)
      };
      setQuestions(sortQuestionsByType([...questions, newQuestion]));
      setEditingQuestion(newQuestion.id);
      return;
    }

    const getDefaultScore = (type: string) => {
      switch (type) {
        case 'single_choice': return 2;
        case 'judge': return 2;
        case 'multi_choice': return 5;
        case 'coding': return 15;
        case 'scratch_coding': return 15;
        default: return 0;
      }
    };

    const newQuestion: Question = {
      id: Date.now().toString(),
      type: typeItem.type,
      title: '',
      placeholder: typeItem.placeholder,
      required: true,
      inputType: '文本',
      options: typeItem.type === 'single_choice' ? [
        { id: 'A', label: 'A', text: '', isCorrect: true },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false }
      ] : typeItem.type === 'judge' ? [
        { id: 'A', label: 'A', text: '正确', isCorrect: true },
        { id: 'B', label: 'B', text: '错误', isCorrect: false }
      ] : typeItem.type === 'multi_choice' ? [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false }
      ] : [],
      score: getDefaultScore(typeItem.type),
      showAnswer: false,
      titleImages: [],
      titleImageWidths: [],
      codeTemplate: '',
      codeLanguage: typeItem.type === 'python_coding' ? 'python' : typeItem.type === 'cpp_coding' ? 'cpp' : '',
      allowFileUpload: false,
      maxFileSize: 10,
      gradingMode: 'ai',
      scoringPoints: '',
      answerText: '',
      answerImages: [],
      answerImageWidths: [],
      explanation: ''
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
    setEditingQuestion(newQuestion.id);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const updateOption = (questionId: string, optionId: string, updates: Partial<Option>) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) =>
            opt.id === optionId ? { ...opt, ...updates } : opt
          )
        };
      }
      return q;
    }));
  };

  const setCorrectAnswer = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) => ({
            ...opt,
            isCorrect: opt.id === optionId
          }))
        };
      }
      return q;
    }));
  };

  const toggleCorrectAnswer = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) =>
            opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt
          )
        };
      }
      return q;
    }));
  };

  const removeQuestion = (id: string) => {
    setQuestions(sortQuestionsByType(questions.filter(q => q.id !== id)));
    if (editingQuestion === id) setEditingQuestion(null);
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      setQuestions(newQuestions);
    } else if (direction === 'down' && index < questions.length - 1) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setQuestions(newQuestions);
    }
  };

  const handleDragStart = (e: React.DragEvent, question: Question, index: number) => {
    setDraggedQuestion(question);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedQuestion && questions[index].type === draggedQuestion.type) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (dragIndex === dropIndex) {
      setDraggedQuestion(null);
      setDragOverIndex(null);
      return;
    }

    const draggedQ = questions[dragIndex];
    const targetQ = questions[dropIndex];

    if (draggedQ.type !== targetQ.type) {
      setDraggedQuestion(null);
      setDragOverIndex(null);
      return;
    }

    const newQuestions = [...questions];
    newQuestions.splice(dragIndex, 1);
    newQuestions.splice(dropIndex, 0, draggedQ);
    setQuestions(newQuestions);
    setDraggedQuestion(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
    setDragOverIndex(null);
  };

  const duplicateQuestion = (question: Question) => {
    const newQuestion = {
      ...question,
      id: Date.now().toString(),
      title: question.title + ' (复制)'
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: string[] = [];
    let processedCount = 0;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newImages.push(event.target?.result as string);
        processedCount++;
        if (processedCount === files.length) {
          setUploadedImages([...uploadedImages, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const getImageUrl = (img: string | undefined): string => {
    if (!img) return '';
    if (img.startsWith('/sb-api/')) return img;
    if (img.startsWith('/storage/')) return `${getSupabaseUrl()}${img}`;
    return img;
  };

  // Scratch配置更新辅助函数
  const updateScratchConfig = (questionId: string, configUpdates: any) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.scratchConfig) return;
    
    updateQuestion(questionId, {
      scratchConfig: { ...question.scratchConfig, ...configUpdates }
    });
  };

  // 算法题配置组件
  const AlgorithmConfigPanel = ({ question }: { question: Question }) => {
    return null;
  };

  // 操作题配置组件
  const OperationConfigPanel = ({ question }: { question: Question }) => {
    const config = question.scratchConfig as OperationConfig;
    if (!config) return null;

    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
            <MousePointer className="w-4 h-4" />
            操作题判分配置
          </h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">必需角色（用逗号分隔）</label>
              <input
                type="text"
                value={config.requiredSprites?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  requiredSprites: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: 小猫, 小鸟"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">必需脚本（用逗号分隔）</label>
              <input
                type="text"
                value={config.requiredScripts?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  requiredScripts: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: event_whenflagclicked, motion_movesteps"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">目标X坐标</label>
                <input
                  type="number"
                  value={config.targetState?.x || 0}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    targetState: { ...config.targetState, x: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">目标Y坐标</label>
                <input
                  type="number"
                  value={config.targetState?.y || 0}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    targetState: { ...config.targetState, y: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">移动积木类型（用逗号分隔）</label>
              <input
                type="text"
                value={config.movementBlocks?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  movementBlocks: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: motion_movesteps, motion_goto, motion_glideto"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">位置容差（像素）</label>
              <input
                type="number"
                value={config.tolerance || 10}
                onChange={(e) => updateScratchConfig(question.id, { 
                  tolerance: parseInt(e.target.value) || 10 
                })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 创意题配置组件
  const CreativeConfigPanel = ({ question }: { question: Question }) => {
    const config = question.scratchConfig as CreativeConfig;
    if (!config) return null;

    return (
      <div className="space-y-4">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            创意题判分配置
          </h4>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">最少角色数</label>
                <input
                  type="number"
                  value={config.minSprites || 1}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    minSprites: parseInt(e.target.value) || 1 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">最少脚本数</label>
                <input
                  type="number"
                  value={config.minScripts || 1}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    minScripts: parseInt(e.target.value) || 1 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">必需元素（用逗号分隔）</label>
              <input
                type="text"
                value={config.requiredElements?.join(',') || ''}
                onChange={(e) => updateScratchConfig(question.id, { 
                  requiredElements: e.target.value.split(',').filter(s => s.trim()) 
                })}
                placeholder="如: backdrop, sound, costume"
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
              />
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">功能权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.functionalityWeight || 0.4}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    functionalityWeight: parseFloat(e.target.value) || 0.4 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">创意权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.creativityWeight || 0.3}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    creativityWeight: parseFloat(e.target.value) || 0.3 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">技术权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.techniqueWeight || 0.2}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    techniqueWeight: parseFloat(e.target.value) || 0.2 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">美观权重</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={config.aestheticsWeight || 0.1}
                  onChange={(e) => updateScratchConfig(question.id, { 
                    aestheticsWeight: parseFloat(e.target.value) || 0.1 
                  })}
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Scratch题型配置渲染
  const renderScratchConfig = (question: Question) => {
    switch (question.scratchSubtype) {
      case 'algorithm':
        return <AlgorithmConfigPanel question={question} />;
      case 'operation':
        return <OperationConfigPanel question={question} />;
      case 'creative':
        return <CreativeConfigPanel question={question} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-lg text-gray-800">题型</h2>
          </div>

          <div className="p-4 bg-blue-50 border-b border-blue-100">
            <h3 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              题型统计
            </h3>
            {/* 答题卡样式 - 题号网格布局 */}
            <div className="space-y-3">
              {(() => {
                // 按题型分组
                const groups: { type: string; name: string; questions: Question[] }[] = [];
                const typeOrder = ['single_choice', 'judge', 'multi_choice', 'python_coding', 'cpp_coding', 'scratch_coding'];
                const typeNames: Record<string, string> = {
                  single_choice: '单选题',
                  judge: '判断题',
                  multi_choice: '多选题',
                  python_coding: 'Python编程题',
                  cpp_coding: 'C++编程题',
                  scratch_coding: 'Scratch编程题'
                };
                const typeLabels = ['一', '二', '三', '四', '五', '六'];
                // 默认分值
                const defaultScores: Record<string, number> = {
                  single_choice: 2,
                  judge: 2,
                  multi_choice: 3,
                  python_coding: 15,
                  cpp_coding: 15,
                  scratch_coding: 15
                };
                // 分值选项
                const scoreOptions = [1, 2, 3, 5, 10, 15, 20];

                // 先按顺序添加有题目的题型
                typeOrder.forEach((type, idx) => {
                  const typeQuestions = questions.filter(q => q.type === type);
                  if (typeQuestions.length > 0) {
                    groups.push({
                      type,
                      name: typeNames[type],
                      questions: typeQuestions
                    });
                  }
                });

                // 添加其他未识别的题型
                const otherQuestions = questions.filter(q => !typeOrder.includes(q.type));
                if (otherQuestions.length > 0) {
                  groups.push({
                    type: 'other',
                    name: '其他题型',
                    questions: otherQuestions
                  });
                }

                // 计算每个题型之前的题目数量，用于确定全局题号
                let questionOffset = 0;

                // 批量更新某类题型分值
                const updateTypeScore = (type: string, score: number) => {
                  setQuestions(questions.map(q => q.type === type ? { ...q, score } : q));
                };

                return groups.map((group, groupIdx) => {
                  const groupQuestions = group.questions;
                  const startOffset = questionOffset;
                  questionOffset += groupQuestions.length;
                  // 获取当前题型的分值（取第一题的分值，如果没有则使用默认值）
                  const currentScore = groupQuestions[0]?.score || defaultScores[group.type] || 2;

                  return (
                    <div key={group.type} className="mb-3">
                      <div className="flex items-center justify-between text-xs font-medium text-gray-700 mb-2">
                        <span>{typeLabels[groupIdx]}、{group.name}（共{groupQuestions.length}题）</span>
                        <div className="flex items-center gap-1">
                          <select
                            value={currentScore}
                            onChange={(e) => updateTypeScore(group.type, parseInt(e.target.value))}
                            className="text-xs px-1 py-0.5 border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-500"
                          >
                            {scoreOptions.map(score => (
                              <option key={score} value={score}>{score}分</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-1.5">
                        {groupQuestions.map((q, idx) => {
                          const globalIndex = startOffset + idx;
                          const isEditing = editingQuestion === q.id;
                          const handleQuestionClick = () => {
                            setTimeout(() => {
                              const questionElement = document.getElementById(`question-${q.id}`);
                              if (questionElement) {
                                questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          };

                          return (
                            <button
                              key={q.id}
                              onClick={handleQuestionClick}
                              className={`aspect-square w-full text-xs font-medium rounded border flex items-center justify-center transition-all ${
                                isEditing
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                              }`}
                            >
                              {String(globalIndex + 1).padStart(2, '0')}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            {/* 总计和总分 */}
            <div className="mt-4 pt-3 border-t border-blue-200 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">总计：</span>
                <span className="font-bold text-blue-600">{questions.length} 道</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">总分：</span>
                <span className="font-bold text-red-600">{questions.reduce((sum, q) => sum + (q.score || 0), 0)} 分</span>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {questionTypes.map((category) => (
              <div key={category.category} className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">{category.category}</h3>
                <div className="space-y-1">
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={`${item.type}-${item.scratchSubtype || 'default'}`}
                        onClick={() => addQuestion(item)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/admin/papers')}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">试卷名称:</span>
                  <div className="px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 w-80 truncate">
                    {title || '请设置试卷信息'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">备注:</span>
                  <div className="relative">
                    <input
                      type="text"
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder="请输入备注"
                      className="px-3 py-1.5 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 w-72 focus:outline-none focus:border-blue-500"
                    />
                    {remark && (
                      <button
                        onClick={() => setRemark('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="清空备注"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowClearConfirmModal(true)}
                  disabled={isSaving}
                  className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Eraser className="w-4 h-4" />
                  清空
                </button>
                <button
                  onClick={() => {
                    const editorState = {
                      title,
                      description,
                      questions,
                      paperSettings,
                      selectedCategory,
                      isPendingPaper,
                      pendingPaperId,
                      editPaperId
                    };
                    localStorage.setItem('paperEditorState', JSON.stringify(editorState));
                    const previewData = {
                      paperId: 'editor-preview',
                      paperTitle: title || '试卷预览',
                      questions: questions.map(q => ({
                        id: q.id,
                        type: q.type,
                        title: q.title,
                        options: q.options.map(o => ({ id: o.id, text: o.text, isCorrect: o.isCorrect, image: o.image, imageWidth: o.imageWidth || 100 })),
                        score: q.score,
                        explanation: q.explanation || q.answerAnalysis,
                        titleImages: q.titleImages,
                        titleImageWidths: q.titleImageWidths
                      })),
                      totalScore: questions.reduce((sum, q) => sum + (q.score || 0), 0),
                      isEditorPreview: true
                    };
                    navigate('/quiz/editor-preview?mode=editor-preview', { state: { previewData } });
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Eye className="w-4 h-4" />
                  试卷预览
                </button>
                <button
                  onClick={() => { setSaveConfirmAction('draft'); setShowSaveConfirmModal(true); }}
                  disabled={isSaving}
                  className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      保存中...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      暂存为草稿
                    </>
                  )}
                </button>
                <button
                  onClick={() => { setSaveConfirmAction('saved'); setShowSaveConfirmModal(true); }}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      保存中...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      保存为试卷
                    </>
                  )}
                </button>
                <button
                  onClick={() => { setSaveConfirmAction('published'); setShowSaveConfirmModal(true); }}
                  disabled={isSaving}
                  className="px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      保存中...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      保存并发布
                    </>
                  )}
                </button>
                {isPendingPaper && pendingPaperId && (
                  <button
                    onClick={publishPendingPaper}
                    disabled={isSaving}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                        发布中...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        一键发布
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">年份:</span>
                <select
                  value={paperSettings.year}
                  onChange={(e) => setPaperSettings({...paperSettings, year: e.target.value})}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">请选择</option>
                  {years.filter(y => y).map(year => <option key={year} value={year}>{year}年</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">月份:</span>
                <select
                  value={paperSettings.month}
                  onChange={(e) => setPaperSettings({...paperSettings, month: e.target.value})}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">请选择</option>
                  <option value="0">空</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => <option key={month} value={String(month)}>{month}月</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">考评体系:</span>
                <select
                  value={paperSettings.organization}
                  onChange={(e) => setPaperSettings({...paperSettings, organization: e.target.value, categoryType: '', level_text: ''})}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">请选择</option>
                  {orgs.map(org => <option key={org.id} value={org.name}>{org.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">课程分类:</span>
                <select
                  value={paperSettings.categoryType}
                  onChange={(e) => setPaperSettings({...paperSettings, categoryType: e.target.value, level_text: ''})}
                  disabled={!paperSettings.organization}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">请选择</option>
                  {filteredCats.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">级别分类:</span>
                <select
                  value={paperSettings.level_text}
                  onChange={(e) => setPaperSettings({...paperSettings, level_text: e.target.value})}
                  disabled={!paperSettings.categoryType}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">请选择</option>
                  {filteredLevels.map(level => (
                    <option key={level.id} value={level.name}>{level.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">试卷类型:</span>
                <select
                  value={paperSettings.source}
                  onChange={(e) => setPaperSettings({...paperSettings, source: e.target.value})}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">请选择</option>
                  <option value="真题">真题</option>
                  <option value="模拟题">模拟题</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">免费/付费:</span>
                <select
                  value={paperSettings.nature}
                  onChange={(e) => setPaperSettings({...paperSettings, nature: e.target.value})}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">请选择</option>
                  {natures.map(nature => <option key={nature} value={nature}>{nature}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">考试时长:</span>
                <select
                  value={paperSettings.duration}
                  onChange={(e) => setPaperSettings({...paperSettings, duration: e.target.value})}
                  className="px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">请选择</option>
                  <option value="30">30分钟</option>
                  <option value="60">60分钟</option>
                  <option value="90">90分钟</option>
                  <option value="120">120分钟</option>
                  <option value="150">150分钟</option>
                  <option value="180">180分钟</option>
                </select>
              </div>
            </div>
          </div>


          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-3xl mx-auto">
              {questions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">点击左侧题型添加题目</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <div
                      key={question.id}
                      id={`question-${question.id}`}
                      className={`bg-white rounded-lg border ${editingQuestion === question.id ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'} transition-all ${dragOverIndex === index ? 'ring-2 ring-blue-400 bg-blue-50' : ''} ${draggedQuestion?.id === question.id ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e as any, question, index)}
                            onDragOver={(e) => handleDragOver(e as any, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e as any, index)}
                            onDragEnd={handleDragEnd}
                            className="cursor-grab active:cursor-grabbing p-1.5 hover:bg-blue-100 hover:text-blue-600 rounded transition-colors group"
                            title="拖拽排序（仅限同题型）"
                          >
                            <GripVertical className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                          <span className="text-sm text-gray-700">
                            {getQuestionTypeLabel(question.type, question.scratchSubtype)}
                          </span>
                          {question.scratchSubtype && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600">
                              {question.scratchSubtype === 'algorithm' && '算法'}
                              {question.scratchSubtype === 'operation' && '操作'}
                              {question.scratchSubtype === 'creative' && '创意'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => moveQuestion(index, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveQuestion(index, 'down')}
                            disabled={index === questions.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => duplicateQuestion(question)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeQuestion(question.id)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {editingQuestion === question.id ? (
                        <div className="p-4">
                          {/* 题目名称输入框 */}
                          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                                <span className="text-xs font-bold text-indigo-600">1</span>
                              </div>
                              <span className="text-sm font-semibold text-gray-800">题目名称</span>
                              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">管理用</span>
                            </div>
                            <input
                              type="text"
                              value={question.name || ''}
                              onChange={(e) => updateQuestion(question.id, { name: e.target.value })}
                              placeholder="请输入题目名称（用于后台列表识别）"
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-sm"
                            />
                          </div>

                          <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-xs font-bold text-blue-600">2</span>
                              </div>
                              <span className="text-sm font-semibold text-gray-800">题干区</span>
                              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">学生可见</span>
                            </div>
                            <QuillEditorWithImageResize
                              value={question.title}
                              onChange={(content, widths) => {
                                if (widths) {
                                  updateQuestion(question.id, { title: content, titleImageWidths: widths });
                                } else {
                                  updateQuestion(question.id, { title: content });
                                }
                              }}
                              questionId={question.id}
                              imageWidths={question.titleImageWidths || []}
                            />

                            {(question.titleImages || []).length > 0 && !question.title?.includes('<img') && (
                              <div className="mt-3 flex flex-wrap gap-3">
                                {(question.titleImages || []).map((img, imgIndex) => (
                                  <div key={imgIndex} className="relative group">
                                    <img
                                      src={getImageUrl(img)}
                                      alt={`题干图片${imgIndex + 1}`}
                                      loading="lazy"
                                      className="rounded-lg border border-gray-200"
                                      style={{ width: `${question.titleImageWidths?.[imgIndex] || 50}%`, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                    />
                                    <div className="absolute top-2 left-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                      {imgIndex + 1}
                                    </div>
                                    <button
                                      onClick={() => {
                                        const newImages = [...(question.titleImages || [])];
                                        const newWidths = [...(question.titleImageWidths || [])];
                                        newImages.splice(imgIndex, 1);
                                        newWidths.splice(imgIndex, 1);
                                        updateQuestion(question.id, { titleImages: newImages, titleImageWidths: newWidths });
                                      }}
                                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                    <div className="absolute bottom-2 left-2 right-2 bg-white/90 rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-600">缩放:</span>
                                        <input
                                          type="range"
                                          min="1"
                                          max="100"
                                          value={question.titleImageWidths?.[imgIndex] || 50}
                                          onChange={(e) => {
                                            const newWidths = [...(question.titleImageWidths || [])];
                                            newWidths[imgIndex] = parseInt(e.target.value);
                                            updateQuestion(question.id, { titleImageWidths: newWidths });
                                          }}
                                          className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <span className="text-xs text-gray-600 w-12 text-right">{question.titleImageWidths?.[imgIndex] || 50}%</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {question.type === 'single_choice' && (() => {
                            const imgOpts = question.options.filter(o => o.image);
                            const currentScale = (() => {
                              if (imgOpts.length === 0) return 100;
                              const first = imgOpts[0].imageWidth || 100;
                              const allSame = imgOpts.every(o => (o.imageWidth || 100) === first);
                              return allSame ? first : 100;
                            })();
                            return (
                              <>
                                {imgOpts.length > 0 && (
                                  <div className="mt-4 flex items-center gap-1">
                                    <span className="text-xs text-gray-500 mr-1">选项缩放:</span>
                                    {[50, 75, 100].map(scale => (
                                      <button
                                        key={scale}
                                        onClick={() => {
                                          const newOptions = question.options.map(o =>
                                            o.image ? { ...o, imageWidth: scale } : o
                                          );
                                          updateQuestion(question.id, { options: newOptions });
                                        }}
                                        className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                                          currentScale === scale
                                            ? 'bg-blue-500 text-white shadow-sm'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                      >
                                        {scale}%
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-2 space-y-3">
                                  {question.options.map((option, optIndex) => (
                                <div key={option.id} className="border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setCorrectAnswer(question.id, option.id)}
                                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${option.isCorrect ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}
                                    >
                                      {option.isCorrect && <Check className="w-4 h-4 text-white" />}
                                    </button>
                                    <span className="text-sm font-medium text-gray-600 w-6">{option.label}.</span>
                                    <input
                                      type="text"
                                      value={option.text}
                                      onChange={(e) => updateOption(question.id, option.id, { text: e.target.value })}
                                      className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                    <button
                                      onClick={() => setImageUploadTarget({ questionId: question.id, optionId: option.id })}
                                      className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"
                                      title="上传图片"
                                    >
                                      <ImageIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        const newOptions = question.options.filter(o => o.id !== option.id).map((o, idx) => ({ ...o, id: String.fromCharCode(65 + idx), label: String.fromCharCode(65 + idx) }));
                                        updateQuestion(question.id, { options: newOptions });
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  {option.image && (
                                    <div className="mt-2">
                                      <div className="flex items-start gap-2">
                                        <div className="flex-1">
                                          <img
                                            src={getImageUrl(option.image)}
                                            alt="选项图片"
                                            className="border rounded"
                                            loading="lazy"
                                            style={{ width: `${option.imageWidth || 100}%`, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                          />
                                        </div>
                                        <div className="flex flex-col gap-1 w-32">
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="range"
                                              min="1"
                                              max="100"
                                              value={option.imageWidth || 100}
                                              onChange={(e) => updateOption(question.id, option.id, { imageWidth: parseInt(e.target.value) })}
                                              className="w-full"
                                            />
                                            <input
                                              type="number"
                                              min="1"
                                              max="100"
                                              value={option.imageWidth || 100}
                                              onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (!isNaN(val) && val >= 1 && val <= 100) {
                                                  updateOption(question.id, option.id, { imageWidth: val });
                                                }
                                              }}
                                              className="w-8 h-4 text-xs text-center border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                            />
                                            <span className="text-xs text-gray-500">%</span>
                                          </div>
                                          <button
                                            onClick={() => updateOption(question.id, option.id, { image: undefined, imageWidth: undefined })}
                                            className="text-xs text-red-500 hover:text-red-600"
                                          >
                                            删除图片
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              <div className="mt-4">
                                <div className="text-sm font-medium text-gray-700 mb-2">答案解析（学生提交试卷后可见）</div>
                                <textarea
                                  value={question.explanation || ''}
                                  onChange={(e) => updateQuestion(question.id, { explanation: e.target.value })}
                                  placeholder="请输入答案解析，学生提交试卷后将看到此内容..."
                                  className="w-full h-24 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
                                  spellCheck={false}
                                />
                              </div>

                              <button
                                onClick={() => {
                                  const newLabel = String.fromCharCode(65 + question.options.length);
                                  updateQuestion(question.id, {
                                    options: [...question.options, { id: newLabel, label: newLabel, text: '', isCorrect: false }]
                                  });
                                }}
                                className="flex items-center gap-1 text-blue-500 text-sm hover:text-blue-600 mt-2"
                              >
                                <Plus className="w-4 h-4" />
                                添加选项
                              </button>
                            </div>
                              </>
                            );
                          })()}

                          {question.type === 'judge' && (
                            <div className="mt-4 space-y-3">
                              {question.options.map((option) => (
                                <div key={option.id} className="border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setCorrectAnswer(question.id, option.id)}
                                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${option.isCorrect ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}
                                    >
                                      {option.isCorrect && <Check className="w-4 h-4 text-white" />}
                                    </button>
                                    <span className="text-sm font-medium text-gray-600 w-6">{option.label}.</span>
                                    <input
                                      type="text"
                                      value={option.text}
                                      onChange={(e) => updateOption(question.id, option.id, { text: e.target.value })}
                                      placeholder="选项内容"
                                      className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {question.type === 'judge' && (
                            <div className="mt-4">
                              <div className="text-sm font-medium text-gray-700 mb-2">答案解析（学生提交试卷后可见）</div>
                              <textarea
                                value={question.explanation || ''}
                                onChange={(e) => updateQuestion(question.id, { explanation: e.target.value })}
                                placeholder="请输入答案解析，学生提交试卷后将看到此内容..."
                                className="w-full h-24 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
                                spellCheck={false}
                              />
                            </div>
                          )}

                          {question.type === 'multi_choice' && (() => {
                            const imgOpts = question.options.filter(o => o.image);
                            const currentScale = (() => {
                              if (imgOpts.length === 0) return 100;
                              const first = imgOpts[0].imageWidth || 100;
                              const allSame = imgOpts.every(o => (o.imageWidth || 100) === first);
                              return allSame ? first : 100;
                            })();
                            return (
                              <>
                                {imgOpts.length > 0 && (
                                  <div className="mt-4 flex items-center gap-1">
                                    <span className="text-xs text-gray-500 mr-1">选项缩放:</span>
                                    {[50, 75, 100].map(scale => (
                                      <button
                                        key={scale}
                                        onClick={() => {
                                          const newOptions = question.options.map(o =>
                                            o.image ? { ...o, imageWidth: scale } : o
                                          );
                                          updateQuestion(question.id, { options: newOptions });
                                        }}
                                        className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                                          currentScale === scale
                                            ? 'bg-blue-500 text-white shadow-sm'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                      >
                                        {scale}%
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-2 space-y-3">
                                  {question.options.map((option, optIndex) => (
                                <div key={option.id} className="border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => toggleCorrectAnswer(question.id, option.id)}
                                      className={`w-6 h-6 rounded border-2 flex items-center justify-center ${option.isCorrect ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}
                                    >
                                      {option.isCorrect && <Check className="w-4 h-4 text-white" />}
                                    </button>
                                    <span className="text-sm font-medium text-gray-600 w-6">{option.label}.</span>
                                    <input
                                      type="text"
                                      value={option.text}
                                      onChange={(e) => updateOption(question.id, option.id, { text: e.target.value })}
                                      placeholder={`选项${optIndex + 1}`}
                                      className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                    <button
                                      onClick={() => setImageUploadTarget({ questionId: question.id, optionId: option.id })}
                                      className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"
                                      title="上传图片"
                                    >
                                      <ImageIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        const newOptions = question.options.filter(o => o.id !== option.id).map((o, idx) => ({ ...o, id: String.fromCharCode(65 + idx), label: String.fromCharCode(65 + idx) }));
                                        updateQuestion(question.id, { options: newOptions });
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  {option.image && (
                                    <div className="mt-2">
                                      <div className="flex items-start gap-2">
                                        <div className="flex-1">
                                          <img
                                            src={getImageUrl(option.image)}
                                            alt="选项图片"
                                            className="border rounded"
                                            loading="lazy"
                                            style={{ width: `${option.imageWidth || 100}%`, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                          />
                                        </div>
                                        <div className="flex flex-col gap-1 w-32">
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="range"
                                              min="1"
                                              max="100"
                                              value={option.imageWidth || 100}
                                              onChange={(e) => updateOption(question.id, option.id, { imageWidth: parseInt(e.target.value) })}
                                              className="w-full"
                                            />
                                            <input
                                              type="number"
                                              min="1"
                                              max="100"
                                              value={option.imageWidth || 100}
                                              onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (!isNaN(val) && val >= 1 && val <= 100) {
                                                  updateOption(question.id, option.id, { imageWidth: val });
                                                }
                                              }}
                                              className="w-8 h-4 text-xs text-center border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                            />
                                            <span className="text-xs text-gray-500">%</span>
                                          </div>
                                          <button
                                            onClick={() => updateOption(question.id, option.id, { image: undefined, imageWidth: undefined })}
                                            className="text-xs text-red-500 hover:text-red-600"
                                          >
                                            删除图片
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  const newLabel = String.fromCharCode(65 + question.options.length);
                                  updateQuestion(question.id, {
                                    options: [...question.options, { id: newLabel, label: newLabel, text: '', isCorrect: false }]
                                  });
                                }}
                                className="flex items-center gap-1 text-blue-500 text-sm hover:text-blue-600 mt-2"
                              >
                                <Plus className="w-4 h-4" />
                                添加选项
                              </button>
                            </div>
                              </>
                            );
                          })()}

                          {question.type === 'multi_choice' && (
                            <div className="mt-4">
                              <div className="text-sm font-medium text-gray-700 mb-2">答案解析（学生提交试卷后可见）</div>
                              <textarea
                                value={question.explanation || ''}
                                onChange={(e) => updateQuestion(question.id, { explanation: e.target.value })}
                                placeholder="请输入答案解析，学生提交试卷后将看到此内容..."
                                className="w-full h-24 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
                                spellCheck={false}
                              />
                            </div>
                          )}

                          {(question.type === 'coding' || question.type === 'python_coding' || question.type === 'cpp_coding' || question.type === 'scratch_coding') && (
                            <div className="mt-4 space-y-4">
                              {question.type === 'scratch_coding' && (
                                <>
                                  {/* Scratch题型配置 - 子类型选项卡切换 */}
                                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                                    {/* 子类型选项卡 */}
                                    <div className="flex items-center gap-2 mb-4 border-b border-gray-200 pb-3">
                                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                                        <span className="text-xs font-bold text-indigo-600">2</span>
                                      </div>
                                      <span className="text-sm font-semibold text-gray-800 mr-4">题型配置</span>
                                      <div className="flex gap-1">
                                        {scratchSubTabs.map((tab) => {
                                          const TabIcon = tab.icon;
                                          const isActive = question.scratchSubtype === tab.key;
                                          return (
                                            <button
                                              key={tab.key}
                                              onClick={() => {
                                                updateQuestion(question.id, {
                                                  scratchSubtype: tab.key as ScratchQuestionSubtype,
                                                  scratchConfig: createDefaultConfig(tab.key as ScratchQuestionSubtype)
                                                });
                                              }}
                                              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                                                isActive
                                                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-200 shadow-sm'
                                                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                                              }`}
                                            >
                                              <TabIcon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                                              {tab.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full ml-auto">AI评卷用</span>
                                    </div>

                                    {/* 根据子类型渲染不同配置面板 */}
                                    {renderScratchConfig(question)}
                                  </div>

                                  {/* 参考SB3文件上传 */}
                                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                                        <span className="text-xs font-bold text-amber-600">3</span>
                                      </div>
                                      <span className="text-sm font-semibold text-gray-800">参考答案SB3</span>
                                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">AI评卷用</span>
                                    </div>
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                      {question.referenceSb3Url ? (
                                        <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                                          <div className="flex items-center gap-2">
                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                            <span className="text-sm text-green-700">已上传参考文件</span>
                                          </div>
                                          <button
                                            onClick={() => updateQuestion(question.id, { referenceSb3Url: undefined })}
                                            className="text-red-500 hover:text-red-600 text-sm"
                                          >
                                            删除
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                          <p className="text-gray-600 mb-2">上传参考SB3文件</p>
                                          <p className="text-xs text-gray-400 mb-4">用于自动判分对比</p>
                                          <input
                                            type="file"
                                            accept=".sb3"
                                            onChange={async (e) => {
                                              const file = e.target.files?.[0];
                                              if (!file) return;
                                              
                                              try {
                                                const { data: { user } } = await supabase.auth.getUser();
                                                if (!user) throw new Error('请先登录');
                                                
                                                const filePath = `reference-sb3/${user.id}/${Date.now()}_${file.name}`;
                                                const { data, error } = await supabase.storage
                                                  .from('scratch-assets')
                                                  .upload(filePath, file, { contentType: 'application/zip' });

                                                if (error) throw error;

                                                const { data: { publicUrl } } = supabase.storage
                                                  .from('scratch-assets')
                                                  .getPublicUrl(filePath);
                                                
                                                updateQuestion(question.id, { referenceSb3Url: publicUrl });
                                              } catch (err) {
                                                
                                                alert('上传失败: ' + (err as Error).message);
                                              }
                                            }}
                                            className="hidden"
                                            id={`sb3-upload-${question.id}`}
                                          />
                                          <label
                                            htmlFor={`sb3-upload-${question.id}`}
                                            className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition-colors"
                                          >
                                            选择文件
                                          </label>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* coding/python_coding/cpp_coding 题型 - 答案提交配置区 */}
                              {(question.type === 'coding' || question.type === 'python_coding' || question.type === 'cpp_coding') && (
                                <div className="bg-white border border-gray-200 rounded-xl p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                                      <span className="text-xs font-bold text-purple-600">3</span>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-800">答案提交</span>
                                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">学生可见</span>
                                    <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">学生提交</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mb-3">此区域由学生作答时上传答案，老师只需配置允许的答题方式</p>
                                  <div className="space-y-3">
                                    {question.type === 'coding' && (
                                      <>
                                        <div className="flex items-center gap-4">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={question.allowImageUpload !== false}
                                              onChange={(e) => updateQuestion(question.id, { allowImageUpload: e.target.checked })}
                                              className="w-4 h-4 text-blue-500 rounded"
                                            />
                                            <span className="text-sm text-gray-600">允许上传图片</span>
                                          </label>
                                          {question.allowImageUpload !== false && (
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm text-gray-600">图片大小上限:</span>
                                              <select
                                                value={question.maxImageSize || 5}
                                                onChange={(e) => updateQuestion(question.id, { maxImageSize: parseInt(e.target.value) })}
                                                className="px-2 py-1 border rounded text-sm"
                                              >
                                                <option value={1}>1M</option>
                                                <option value={2}>2M</option>
                                                <option value={5}>5M</option>
                                                <option value={10}>10M</option>
                                              </select>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-4">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={question.allowFileUpload !== false}
                                              onChange={(e) => updateQuestion(question.id, { allowFileUpload: e.target.checked })}
                                              className="w-4 h-4 text-blue-500 rounded"
                                            />
                                            <span className="text-sm text-gray-600">允许上传文件</span>
                                          </label>
                                          {question.allowFileUpload !== false && (
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm text-gray-600">最大文件大小:</span>
                                              <select
                                                value={question.maxFileSize || 10}
                                                onChange={(e) => updateQuestion(question.id, { maxFileSize: parseInt(e.target.value) })}
                                                className="px-2 py-1 border rounded text-sm"
                                              >
                                                <option value={5}>5MB</option>
                                                <option value={10}>10MB</option>
                                                <option value={20}>20MB</option>
                                                <option value={50}>50MB</option>
                                              </select>
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    )}
                                    <div className="flex items-center gap-4">
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={question.allowTextInput !== false}
                                          onChange={(e) => updateQuestion(question.id, { allowTextInput: e.target.checked })}
                                          className="w-4 h-4 text-blue-500 rounded"
                                        />
                                        <span className="text-sm text-gray-600">允许输入文本</span>
                                      </label>
                                      {question.allowTextInput !== false && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-600">文本字数上限:</span>
                                          <select
                                            value={question.maxTextLength || 800}
                                            onChange={(e) => updateQuestion(question.id, { maxTextLength: parseInt(e.target.value) })}
                                            className="px-2 py-1 border rounded text-sm"
                                          >
                                            <option value={100}>100字</option>
                                            <option value={200}>200字</option>
                                            <option value={300}>300字</option>
                                            <option value={500}>500字</option>
                                            <option value={800}>800字</option>
                                          </select>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* 区域3: 评分规则区 - 学生提交后可见 */}
                              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                                    <span className="text-xs font-bold text-amber-600">4</span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-800">评分规则</span>
                                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">提交后可见</span>
                                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">AI评卷用</span>
                                </div>
                                <QuillEditorWithImageResize
                                  value={question.scoringPoints || ''}
                                  onChange={(content, widths) => {
                                    if (widths) {
                                      updateQuestion(question.id, { scoringPoints: content, scoringPointsImageWidths: widths });
                                    } else {
                                      updateQuestion(question.id, { scoringPoints: content });
                                    }
                                  }}
                                  questionId={question.id}
                                  imageWidths={question.scoringPointsImageWidths || []}
                                />
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {(question.scoringPointsImages || []).map((img, imgIndex) => (
                                    <div key={imgIndex} className="relative group">
                                      <img
                                        src={getImageUrl(img)}
                                        alt={`评分规则图片${imgIndex + 1}`}
                                        className="w-20 h-20 object-cover rounded-lg border border-amber-200 cursor-pointer hover:opacity-80"
                                        loading="lazy"
                                        onClick={() => openImageViewer(getImageUrl(img), `评分规则图片${imgIndex + 1}`)}
                                      />
                                      <button
                                        onClick={() => {
                                          const newImages = [...(question.scoringPointsImages || [])];
                                          const newWidths = [...(question.scoringPointsImageWidths || [])];
                                          newImages.splice(imgIndex, 1);
                                          newWidths.splice(imgIndex, 1);
                                          updateQuestion(question.id, { scoringPointsImages: newImages, scoringPointsImageWidths: newWidths });
                                        }}
                                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                {(question.scoringPointsImages || []).length < 10 && (
                                  <button
                                    onClick={() => {
                                      setImageUploadDialogConfig({
                                        title: '上传评分规则图片',
                                        target: 'scoringPoints',
                                        questionId: question.id
                                      });
                                      setShowImageUploadDialog(true);
                                    }}
                                    className="flex items-center gap-1 text-amber-600 text-sm hover:text-amber-700"
                                  >
                                    <Plus className="w-4 h-4" />
                                    上传图片 ({(question.scoringPointsImages || []).length}/10)
                                  </button>
                                )}
                              </div>

                              {/* 区域4: 参考答案区 - 学生提交后可见 */}
                              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                    <span className="text-xs font-bold text-green-600">5</span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-800">参考答案</span>
                                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">提交后可见</span>
                                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">AI评卷用</span>
                                </div>
                                <div className="mb-3">
                                  <QuillEditorWithImageResize
                                    value={question.answerText || ''}
                                    onChange={(content, widths) => {
                                      if (widths) {
                                        updateQuestion(question.id, { answerText: content, referenceAnswerImageWidths: widths });
                                      } else {
                                        updateQuestion(question.id, { answerText: content });
                                      }
                                    }}
                                    questionId={question.id}
                                    imageWidths={question.referenceAnswerImageWidths || []}
                                  />
                                </div>
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {(question.referenceAnswerImages || []).map((img, imgIndex) => (
                                    <div key={imgIndex} className="relative group">
                                      <img
                                        src={getImageUrl(img)}
                                        alt={`参考答案图片${imgIndex + 1}`}
                                        className="w-20 h-20 object-cover rounded-lg border border-green-200 cursor-pointer hover:opacity-80"
                                        loading="lazy"
                                        onClick={() => openImageViewer(getImageUrl(img), `参考答案图片${imgIndex + 1}`)}
                                      />
                                      <button
                                        onClick={() => {
                                          const newImages = [...(question.referenceAnswerImages || [])];
                                          const newWidths = [...(question.referenceAnswerImageWidths || [])];
                                          newImages.splice(imgIndex, 1);
                                          newWidths.splice(imgIndex, 1);
                                          updateQuestion(question.id, { referenceAnswerImages: newImages, referenceAnswerImageWidths: newWidths });
                                        }}
                                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                {(question.referenceAnswerImages || []).length < 10 && (
                                  <button
                                    onClick={() => {
                                      setImageUploadDialogConfig({
                                        title: '上传参考答案图片',
                                        target: 'referenceAnswer',
                                        questionId: question.id
                                      });
                                      setShowImageUploadDialog(true);
                                    }}
                                    className="flex items-center gap-1 text-green-600 text-sm hover:text-green-700"
                                  >
                                    <Plus className="w-4 h-4" />
                                    上传图片 ({(question.referenceAnswerImages || []).length}/10)
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-600">分值:</span>
                              <input
                                type="number"
                                value={question.score || 0}
                                onChange={(e) => updateQuestion(question.id, { score: parseInt(e.target.value) || 0 })}
                                className="w-16 px-2 py-1 border border-gray-200 rounded text-sm"
                              />
                            </div>
                            <button
                              onClick={() => setEditingQuestion(null)}
                              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                            >
                              完成
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="px-4 py-3 cursor-pointer hover:bg-gray-50"
                          onClick={() => setEditingQuestion(question.id)}
                        >
                          {question.type === 'scratch_coding' ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: question.title || '点击编辑题目' }} />
                              </div>
                              {question.scratchSubtype && (
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600">
                                    {question.scratchSubtype === 'algorithm' && '算法题'}
                                    {question.scratchSubtype === 'operation' && '操作题'}
                                    {question.scratchSubtype === 'creative' && '创意题'}
                                  </span>
                                  {question.referenceSb3Url && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-50 text-green-600">
                                      已上传参考文件
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {/* 渲染题干文本（不包含img标签） */}
                              <div
                                className="text-sm font-medium text-gray-900"
                                dangerouslySetInnerHTML={{
                                  __html: question.title?.replace(/<img[^>]*>/g, '') || '点击编辑题目'
                                }}
                              />
                              {/* 渲染题干图片 - 优先从title中的img标签提取，否则使用titleImages */}
                              {(() => {
                                const imgUrls: string[] = [];
                                // 从title中提取img标签的src
                                if (question.title?.includes('<img')) {
                                  const imgMatches = question.title.match(/<img[^>]+src="([^"]+)"/g);
                                  if (imgMatches) {
                                    imgMatches.forEach(match => {
                                      const srcMatch = match.match(/src="([^"]+)"/);
                                      if (srcMatch) {
                                        imgUrls.push(getImageUrl(srcMatch[1]));
                                      }
                                    });
                                  }
                                }
                                // 如果title中没有img，则使用titleImages
                                if (imgUrls.length === 0 && (question.titleImages || []).length > 0) {
                                  (question.titleImages || []).forEach(img => {
                                    imgUrls.push(getImageUrl(img));
                                  });
                                }
                                return imgUrls.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {imgUrls.map((imgUrl, imgIndex) => (
                                      <img
                                        key={imgIndex}
                                        src={imgUrl}
                                        alt={`题干图片${imgIndex + 1}`}
                                        className="rounded border border-gray-200"
                                        loading="lazy"
                                        style={{ width: `${question.titleImageWidths?.[imgIndex] || 50}%`, maxWidth: '200px', height: 'auto', objectFit: 'contain' }}
                                      />
                                    ))}
                                  </div>
                                ) : null;
                              })()}
                              {(question.type === 'single_choice' || question.type === 'multi_choice' || question.type === 'judge') && question.options.length > 0 && (() => {
                                const imgOpts = question.options.filter(o => o.image);
                                const currentScale = (() => {
                                  if (imgOpts.length === 0) return 100;
                                  const first = question.optionImageWidths?.[imgOpts[0].id] || imgOpts[0].imageWidth || 100;
                                  const allSame = imgOpts.every(o => (question.optionImageWidths?.[o.id] || o.imageWidth || 100) === first);
                                  return allSame ? first : 100;
                                })();
                                return (
                                  <>
                                    {imgOpts.length > 0 && (
                                      <div className="flex items-center gap-1 mb-2">
                                        <span className="text-xs text-gray-500 mr-1">选项缩放:</span>
                                        {[50, 75, 100].map(scale => (
                                          <button
                                            key={scale}
                                            onClick={() => {
                                              const newWidths: Record<string, number> = { ...question.optionImageWidths };
                                              const newOptions = question.options.map(o => {
                                                if (o.image) {
                                                  newWidths[o.id] = scale;
                                                  return { ...o, imageWidth: scale };
                                                }
                                                return o;
                                              });
                                              updateQuestion(question.id, { optionImageWidths: newWidths, options: newOptions });
                                            }}
                                            className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                                              currentScale === scale
                                                ? 'bg-blue-500 text-white shadow-sm'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                          >
                                            {scale}%
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    <div className="space-y-1">
                                      {question.options.map((option) => {
                                        const optWidth = question.optionImageWidths?.[option.id] || option.imageWidth || 100;
                                        return (
                                          <div key={option.id} className="text-sm">
                                            <div className="flex items-center gap-2">
                                              <span className={`w-5 h-5 rounded-full border flex items-center justify-center text-xs ${option.isCorrect ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 text-gray-500'}`}>
                                                {option.isCorrect ? '✓' : option.label}
                                              </span>
                                              <span className={`${option.isCorrect ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                {option.text || `选项 ${option.label}`}
                                              </span>
                                            </div>
                                            {option.image && (
                                              <div className="mt-1 ml-7">
                                                <img
                                                  src={getImageUrl(option.image)}
                                                  alt="选项图片"
                                                  className="rounded border border-gray-200"
                                                  loading="lazy"
                                                  style={{ width: `${optWidth}%`, maxWidth: '150px', height: 'auto', objectFit: 'contain' }}
                                                />
                                                <div className="flex items-center gap-1 mt-1">
                                                  <input
                                                    type="range"
                                                    min="10"
                                                    max="100"
                                                    step="1"
                                                    value={optWidth}
                                                    onChange={(e) => {
                                                      const val = parseInt(e.target.value);
                                                      const newWidths = { ...question.optionImageWidths, [option.id]: val };
                                                      updateQuestion(question.id, { optionImageWidths: newWidths });
                                                      updateOption(question.id, option.id, { imageWidth: val });
                                                    }}
                                                    className="w-16 h-1 accent-blue-500"
                                                  />
                                                  <input
                                                    type="number"
                                                    min="10"
                                                    max="100"
                                                    value={optWidth}
                                                    onChange={(e) => {
                                                      const val = parseInt(e.target.value);
                                                      if (!isNaN(val) && val >= 10 && val <= 100) {
                                                        const newWidths = { ...question.optionImageWidths, [option.id]: val };
                                                        updateQuestion(question.id, { optionImageWidths: newWidths });
                                                        updateOption(question.id, option.id, { imageWidth: val });
                                                      }
                                                    }}
                                                    className="w-6 h-4 text-xs text-center border border-gray-300 rounded focus:border-blue-500 outline-none"
                                                  />
                                                  <span className="text-xs text-gray-400">%</span>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                );
                              })()}
                              {(question.type === 'coding' || question.type === 'python_coding' || question.type === 'cpp_coding' || question.type === 'scratch_coding') && (
                                <div className="space-y-2 text-sm text-gray-600">
                                  {question.codeTemplate && (
                                    <div className="bg-gray-100 rounded p-2">
                                      <span className="text-xs text-gray-500">代码模板:</span>
                                      <pre className="mt-1 text-xs overflow-x-auto">{question.codeTemplate.substring(0, 100)}{question.codeTemplate.length > 100 ? '...' : ''}</pre>
                                    </div>
                                  )}
                                  {question.answerText && (
                                    <div>
                                      <span className="text-xs text-gray-500">参考答案:</span>
                                      <div className="mt-1 text-xs" dangerouslySetInnerHTML={{ __html: question.answerText }} />
                                    </div>
                                  )}
                                  {(question.referenceAnswerImages || []).length > 0 && (
                                    <div>
                                      <span className="text-xs text-gray-500">参考答案图片:</span>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {(question.referenceAnswerImages || []).map((img, imgIndex) => (
                                          <img
                                            key={imgIndex}
                                            src={getImageUrl(img)}
                                            alt={`参考答案图片${imgIndex + 1}`}
                                            className="rounded border border-gray-200"
                                            loading="lazy"
                                            style={{ width: `${question.referenceAnswerImageWidths?.[imgIndex] || 50}%`, maxWidth: '150px', height: 'auto', objectFit: 'contain' }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              {question.explanation && (
                                <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                                  <span className="font-medium">解析:</span> {question.explanation.substring(0, 80)}{question.explanation.length > 80 ? '...' : ''}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {imageUploadTarget && (
        <ImageUploadModal
          target={imageUploadTarget}
          onClose={() => setImageUploadTarget(null)}
          onUpload={(imageData) => {
            const { questionId, optionId } = imageUploadTarget;
            if (optionId) {
              updateOption(questionId, optionId, { image: imageData, imageWidth: 100 });
            } else {
              const newImages = [...(questions.find(q => q.id === questionId)?.titleImages || []), imageData];
              const newWidths = [...(questions.find(q => q.id === questionId)?.titleImageWidths || []), 50];
              updateQuestion(questionId, { titleImages: newImages, titleImageWidths: newWidths });
            }
            setImageUploadTarget(null);
          }}
          allowMultiple={!imageUploadTarget.optionId}
        />
      )}

      {showImageUploadDialog && imageUploadDialogConfig && (
        <ImageUploadDialog
          isOpen={showImageUploadDialog}
          onClose={() => setShowImageUploadDialog(false)}
          title={imageUploadDialogConfig.title}
          maxCount={10}
          initialImages={(() => {
            const question = questions.find(q => q.id === imageUploadDialogConfig.questionId);
            if (!question) return [];
            switch (imageUploadDialogConfig.target) {
              case 'title': return question.titleImages || [];
              case 'answer': return question.answerImages || [];
              case 'scoringPoints': return question.scoringPointsImages || [];
              case 'referenceAnswer': return question.referenceAnswerImages || [];
              default: return [];
            }
          })()}
          onConfirm={(images) => {
            const question = questions.find(q => q.id === imageUploadDialogConfig.questionId);
            if (!question) return;
            const widths = images.map(() => 100);
            switch (imageUploadDialogConfig.target) {
              case 'title':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  titleImages: images,
                  titleImageWidths: widths
                });
                break;
              case 'answer':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  answerImages: images,
                  answerImageWidths: widths
                });
                break;
              case 'scoringPoints':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  scoringPointsImages: images,
                  scoringPointsImageWidths: widths
                });
                break;
              case 'referenceAnswer':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  referenceAnswerImages: images,
                  referenceAnswerImageWidths: widths
                });
                break;
            }
            setShowImageUploadDialog(false);
          }}
        />
      )}

      {showWordImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">上传Word试卷</h3>
              <button onClick={() => setShowWordImportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">点击上传或拖拽Word文档到此处</p>
              <p className="text-xs text-gray-400">支持 .docx 格式</p>
              <input
                type="file"
                accept=".docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    alert('Word文档上传功能开发中，请选择手动创建题目');
                    setShowWordImportModal(false);
                  }
                }}
                className="hidden"
                id="word-upload"
              />
              <label
                htmlFor="word-upload"
                className="mt-4 inline-block px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600"
              >
                选择文件
              </label>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {saveConfirmAction === 'draft' && '暂存为草稿'}
                {saveConfirmAction === 'saved' && '保存为试卷'}
                {saveConfirmAction === 'published' && '保存并发布'}
              </h3>
              <button onClick={() => setShowSaveConfirmModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              {saveConfirmAction === 'draft' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认将试卷暂存为草稿？</p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                    <p>保存后状态：草稿</p>
                    <p>可见性：仅管理员可见，不会出现在正式试卷列表</p>
                    <p>用途：方便后续继续编辑</p>
                  </div>
                </div>
              )}
              {saveConfirmAction === 'saved' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认将试卷保存为隐藏状态？</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <p>保存后状态：隐藏</p>
                    <p>可见性：仅管理员可见，可用于内部测试</p>
                    <p>用途：待发布或内部测试使用</p>
                  </div>
                </div>
              )}
              {saveConfirmAction === 'published' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认保存并发布试卷？</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p>保存后状态：已发布</p>
                    <p>可见性：学员端可见，可正常答题</p>
                    <p>注意：发布后学员即可看到并参与考试</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveConfirmModal(false)}
                disabled={isSaving}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setShowSaveConfirmModal(false);
                  if (saveConfirmAction === 'draft') {
                    saveAsDraft();
                  } else {
                    savePaper(saveConfirmAction);
                  }
                }}
                disabled={isSaving}
                className={`flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 ${
                  saveConfirmAction === 'draft' ? 'bg-orange-500 hover:bg-orange-600' :
                  saveConfirmAction === 'saved' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {isSaving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                    保存中...
                  </>
                ) : (
                  `确认${saveConfirmAction === 'draft' ? '暂存' : saveConfirmAction === 'saved' ? '保存' : '发布'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-red-600">清空确认</h3>
              <button onClick={() => setShowClearConfirmModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-red-600">
                  <AlertCircle className="w-6 h-6" />
                  <p className="font-medium">确定要清空所有题目吗？</p>
                </div>
                <p className="text-gray-600 text-sm">此操作将删除当前编辑中的所有题目（共 {questions.length} 道），此操作不可撤销。</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  <p>注意：试卷设置信息（年份、考评体系等）将保留不变</p>
                  <p>已保存的草稿或已发布的试卷数据不受影响</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setQuestions([]);
                  setEditingQuestion(null);
                  setShowClearConfirmModal(false);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
